/**
 * İKONLAR — inline SVG, tek renk, `currentColor`.
 *
 * ⚠️ EMOJİ KULLANILMAZ. Üç ayrı sebep:
 *   1. Emoji TAM RENKLİDİR ve tasarım dili "düşük dekorasyon" diyor (§4.2).
 *      Renkler bu üründe SEMANTİK — kırmızı "kritik" demek. Ondan bağımsız
 *      renk taşıyan bir sembol o anlam haritasını bozar.
 *   2. Emoji her platformda FARKLI çizilir. Aynı zil iOS'ta, Android'de ve
 *      Windows'ta başka görünür; ekran görüntüsüyle konuşan bir ekipte bu
 *      gereksiz bir belirsizlik.
 *   3. Sistem emoji fontuna bağımlıdır — hangi glifin var olduğunu garanti
 *      edemeyiz. Inline SVG offline'da da aynı çizilir (T1).
 *
 * Onaylı prototip zaten böyle yapıyor: 50 inline SVG, `viewBox="0 0 24 24"`,
 * `stroke="currentColor"`, `stroke-width="2"`. Burada aynı sözleşme sürdürülüyor.
 *
 * Kütüphane YOK — indirilecek dosya olmaması offline'da ikonun kaybolmamasını
 * garanti eder; sistem font yığınlarını seçme gerekçesinin aynısı.
 */

import type { SVGProps } from 'react';

type IkonProps = SVGProps<SVGSVGElement> & { boyut?: number };

/**
 * ⚠️ `inline-block` VARSAYILAN — Tailwind preflight `svg`'ye `display: block`
 *    verir. Bir metnin başında duran ikon bu yüzden KENDİ SATIRINA düşüyordu:
 *
 *        ⚠
 *        Uygunsuzluk var — yeşil kartı kaldır      ← sahada görüldü (03.08.2026)
 *
 *    Tek tek çağrı yerlerine `className="inline"` yazmak bu hatayı her yeni
 *    kullanımda tekrar üretir; varsayılan burada düzeltiliyor.
 *
 *    `shrink-0`: flex kutusunda ikon ezilmesin.
 *    `align-[-0.125em]`: metin taban çizgisiyle optik hizalama — `middle`
 *    büyük punto metinlerde ikonu yukarı kaçırıyor.
 *
 *    Blok davranışı gereken yerde çağrı tarafı `className="block"` geçer;
 *    sonda geldiği için kazanır.
 */
function Ikon({ boyut = 18, children, className, ...p }: IkonProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={boyut}
      height={boyut}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={`inline-block shrink-0 align-[-0.125em] ${className ?? ''}`}
      {...p}
    >
      {children}
    </svg>
  );
}

/* ---- Kabuk ------------------------------------------------------- */

export function ZilIkon(p: IkonProps) {
  return (
    <Ikon {...p}>
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </Ikon>
  );
}

/* ---- Adım durumları (mobil akordeon + iş detayı) ------------------ */

/** Tamamlandı. */
export function OnayIkon(p: IkonProps) {
  return (
    <Ikon {...p}>
      <path d="M20 6 9 17l-5-5" />
    </Ikon>
  );
}

/** Kilitli — önceki adım bitmeden açılmaz. */
export function KilitIkon(p: IkonProps) {
  return (
    <Ikon {...p}>
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </Ikon>
  );
}

/** Sıradaki adım. */
export function NoktaIkon(p: IkonProps) {
  return (
    <Ikon {...p}>
      <circle cx="12" cy="12" r="4" fill="currentColor" stroke="none" />
    </Ikon>
  );
}

/* ---- İçerik ------------------------------------------------------ */

/** Fotoğraf / kanıt zinciri. */
export function KameraIkon(p: IkonProps) {
  return (
    <Ikon {...p}>
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </Ikon>
  );
}

/**
 * Telefon galerisi — kamera ile AYNI ekranda yan yana durur, bu yüzden
 * silueti kasten farklı: dikdörtgen çerçeve + içinde dağ/güneş. Aynı aileden
 * iki ikon (iki kamera gövdesi) eldivenli parmak hızıyla ayırt edilemezdi.
 */
export function GaleriIkon(p: IkonProps) {
  return (
    <Ikon {...p}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <path d="m21 15-4.5-4.5L7 20" />
    </Ikon>
  );
}

/** İş emri kâğıdı. */
export function BelgeIkon(p: IkonProps) {
  return (
    <Ikon {...p}>
      <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
      <path d="M14 3v6h6" />
    </Ikon>
  );
}

/** Not / açıklama. */
export function NotIkon(p: IkonProps) {
  return (
    <Ikon {...p}>
      <path d="M11 4H4v16h16v-7" />
      <path d="m18.5 2.5 3 3L12 15l-4 1 1-4z" />
    </Ikon>
  );
}

/** Ekip / kişi. */
export function EkipIkon(p: IkonProps) {
  return (
    <Ikon {...p}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    </Ikon>
  );
}

/** Kalkan — güvenlik, rutin kontrol, yeşil kart. */
export function KalkanIkon(p: IkonProps) {
  return (
    <Ikon {...p}>
      <path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6z" />
      <path d="m9 12 2 2 4-4" />
    </Ikon>
  );
}

/** Revizyon — kurulu iskele üzerinde değişiklik. */
export function RevizeIkon(p: IkonProps) {
  return (
    <Ikon {...p}>
      <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
      <path d="M3 21v-5h5" />
    </Ikon>
  );
}

/** Söküm. */
export function SokumIkon(p: IkonProps) {
  return (
    <Ikon {...p}>
      <path d="M3 6h18" />
      <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
      <path d="M19 6v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6" />
    </Ikon>
  );
}

/** İskele / kurulum. */
export function IskeleIkon(p: IkonProps) {
  return (
    <Ikon {...p}>
      <path d="M3 21h18" />
      <path d="M6 21V8l6-4 6 4v13" />
      <path d="M6 13h12" />
    </Ikon>
  );
}

/** Malzeme / depo. */
export function KutuIkon(p: IkonProps) {
  return (
    <Ikon {...p}>
      <path d="M21 8v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8" />
      <path d="M1 3h22v5H1z" />
      <path d="M10 12h4" />
    </Ikon>
  );
}

/** İzin belgesi / imza. */
export function IzinIkon(p: IkonProps) {
  return (
    <Ikon {...p}>
      <path d="M4 4h16v12H5.2L4 17.5z" />
      <path d="M8 9h8M8 12h5" />
    </Ikon>
  );
}

/* ---- Gezinme ----------------------------------------------------- */

export function OkSagIkon(p: IkonProps) {
  return (
    <Ikon {...p}>
      <path d="m9 6 6 6-6 6" />
    </Ikon>
  );
}

export function OkAsagiIkon(p: IkonProps) {
  return (
    <Ikon {...p}>
      <path d="m6 9 6 6 6-6" />
    </Ikon>
  );
}

export function KapatIkon(p: IkonProps) {
  return (
    <Ikon {...p}>
      <path d="M18 6 6 18M6 6l12 12" />
    </Ikon>
  );
}

export function ArtiIkon(p: IkonProps) {
  return (
    <Ikon {...p}>
      <path d="M12 5v14M5 12h14" />
    </Ikon>
  );
}

/** Geri al — "yanlış tik". */
export function GeriAlIkon(p: IkonProps) {
  return (
    <Ikon {...p}>
      <path d="M9 14 4 9l5-5" />
      <path d="M4 9h11a5 5 0 0 1 0 10h-4" />
    </Ikon>
  );
}

/** Uyarı — kritik / güvenli değil. */
export function UyariIkon(p: IkonProps) {
  return (
    <Ikon {...p}>
      <path d="M12 3 2 20h20z" />
      <path d="M12 9v5M12 17.5v.01" />
    </Ikon>
  );
}

/** Saat — bekleme / darboğaz (dikkat kartı, uyarı sınıfı). */
export function SaatIkon(p: IkonProps) {
  return (
    <Ikon {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </Ikon>
  );
}

/** Bilgi — planlı, henüz geç değil. */
export function BilgiIkon(p: IkonProps) {
  return (
    <Ikon {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 8v.01" />
    </Ikon>
  );
}

/* ---- Sidebar --------------------------------------------------- */

/** Pano — kova şeridi metaforu. */
export function PanoIkon(p: IkonProps) {
  return (
    <Ikon {...p}>
      <rect x="3" y="3" width="7" height="9" rx="1" />
      <rect x="14" y="3" width="7" height="5" rx="1" />
      <rect x="14" y="12" width="7" height="9" rx="1" />
      <rect x="3" y="16" width="7" height="5" rx="1" />
    </Ikon>
  );
}

/** Günlük rapor. */
export function RaporIkon(p: IkonProps) {
  return (
    <Ikon {...p}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 9h18M8 13h8M8 16h5" />
    </Ikon>
  );
}

export function AramaIkon(p: IkonProps) {
  return (
    <Ikon {...p}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </Ikon>
  );
}

export function AyarIkon(p: IkonProps) {
  return (
    <Ikon {...p}>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M19.1 4.9 17 7M7 17l-2.1 2.1" />
    </Ikon>
  );
}

export function ListeIkon(p: IkonProps) {
  return (
    <Ikon {...p}>
      <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
    </Ikon>
  );
}

/**
 * SIRALAMA — ok + yanında azalan/artan basamaklar.
 *
 * `yon="asc"` ok YUKARI + basamaklar kısadan uzuna (eskiden yeniye),
 * `yon="desc"` ok AŞAĞI + uzundan kısaya. Tek ikonun dönmesi yerine iki ayrı
 * çizim: saha kullanıcısı 90° dönmüş bir oku okumak zorunda kalmasın.
 */
export function SiralaIkon({ yon, ...p }: IkonProps & { yon: 'asc' | 'desc' }) {
  return (
    <Ikon {...p}>
      {yon === 'asc' ? (
        <>
          <path d="M6 20V4m0 0L3 7m3-3 3 3" />
          <path d="M12 6h3M12 12h6M12 18h9" />
        </>
      ) : (
        <>
          <path d="M6 4v16m0 0 3-3m-3 3-3-3" />
          <path d="M12 6h9M12 12h6M12 18h3" />
        </>
      )}
    </Ikon>
  );
}

/** Hamburger — mobil gezinme menüsü. */
export function MenuIkon(p: IkonProps) {
  return (
    <Ikon {...p}>
      <path d="M4 6h16M4 12h16M4 18h16" />
    </Ikon>
  );
}

export function OkSolIkon(p: IkonProps) {
  return (
    <Ikon {...p}>
      <path d="m15 6-6 6 6 6" />
    </Ikon>
  );
}

/** Dışa aktar — kutudan yukarı çıkan ok. */
export function IndirIkon(p: IkonProps) {
  return (
    <Ikon {...p}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
    </Ikon>
  );
}
